import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useSnackbar } from '@ocx-app/lib/snackbar/snackbar';

import { useShowAttemptTimeoutMessage } from '../../use-show-attempt-timeout-message.hook';
import { useSelectOTPChannelWithModal } from './use-select-otp-channel-with-modal.hook';
import { RequestMagicLinkParams, RequestOtpParams } from '../../signup.types';
import { messages } from '../../signup.messages';

type UseSignupWaitMessageControllerParams = {
  phoneNumber: string;
  isBlockedByTimeout: boolean;
  handleRequestMagicLink(params: RequestMagicLinkParams): Promise<boolean>;
  handleRequestOTP(params: RequestOtpParams): Promise<boolean>;
};

export const useSignupWaitMessageController = (params: UseSignupWaitMessageControllerParams) => {
  const intl = useIntl();
  const snackbar = useSnackbar();
  const { phoneNumber, isBlockedByTimeout, handleRequestMagicLink, handleRequestOTP } = params;
  const showAttemptTimeoutMessage = useShowAttemptTimeoutMessage();
  const { select: selectOTPChanel } = useSelectOTPChannelWithModal();

  const handleResendMagicLink = useCallback(async () => {
    if (isBlockedByTimeout) {
      showAttemptTimeoutMessage();
      return;
    }
    const res = await handleRequestMagicLink({ phoneNumber });
    if (res) {
      snackbar.open(intl.formatMessage(messages['signup:auth-message-resent']), { variant: 'success' });
    }
  }, [handleRequestMagicLink, intl, isBlockedByTimeout, showAttemptTimeoutMessage, snackbar, phoneNumber]);

  const handleLoginViaOTP = useCallback(async () => {
    if (isBlockedByTimeout) {
      showAttemptTimeoutMessage();
      return;
    }
    const otpChannel = await selectOTPChanel();
    if (!otpChannel) {
      return;
    }
    await handleRequestOTP({ phoneNumber, otpChannel });
  }, [handleRequestOTP, isBlockedByTimeout, phoneNumber, selectOTPChanel, showAttemptTimeoutMessage]);

  return { handleResendMagicLink, handleLoginViaOTP };
};
